import { useDeferredValue, useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  useOrderFormStore,
  useOrderFormStoreActions,
} from '@/modules/order/stores/order-form.store'

import { useProductImageOrderData } from '../data/product-image-order.data'
import {
  useProductStore,
  useProductStoreActions,
} from '../stores/product.store'
import type { TProductCardHookResponse } from '../types/product-card-hook-response.type'
import type { TProductCardHook } from '../types/product-card-hook.type'
import { useProducts } from '@/modules/order/hooks/use-products.hook'
import { useProductSearchInputStore } from '@/modules/order/stores/product-search-input.store'
import { useOrderFormCalculation } from '@/modules/order/hooks/use-order-form-calculation.hook'
import useOrderFormParams from '@/modules/order/hooks/use-order-form-params.hook'
import { valueOrDefault } from 'chart.js/dist/helpers/helpers.core'

export const useProductCardHook = ({
  product,
}: TProductCardHook): TProductCardHookResponse => {
  const [isChangeProduct, setIsChangeProduct] = useState(false)
  const [quantityOfProduct, setQuantityOfProduct] = useState(0)
  const deferredQuantity = useDeferredValue(quantityOfProduct)

  const { calculateOrderForm } = useOrderFormCalculation();

  const { utilizaValorSugerido, utilizaTabelaPrecoMobile } = useOrderFormParams()
  const productImageOrderData = useProductImageOrderData()

  const {
    jsonOrderForm,
    isCalculationOrder,
    paramsLayoutOrderForm,
    operationType,
  } = useOrderFormStore()

  const {
    setIsCalculationOrder,
    setJsonOrderForm,
    setUpdatedProductDetails
  } = useOrderFormStoreActions()

  const {
    productsInsertedInOrderToCalculation,
    defaultTabsProductDetails,
    actions: { setDefaultTabsProductDetails },
  } = useProductStore()

  const {
    setProductsInsertedInOrderToCalculation,
    setIsOpenModalDetailsProduct,
    setProductIdSelected,
  } = useProductStoreActions

  const getIndexOfCurrentProduct = () => {
    return jsonOrderForm?.itemsCalculo?.findIndex(
      (item) => item.fkProduto === product?.fkProduto,
    )
  }

  const getProductInOrder = () => {
    return jsonOrderForm?.itemsCalculo?.find(
      (item) => item.fkProduto === product?.fkProduto,
    )
  }

  const insertProductInArrayToCalculation = (fkProduto: string) => {
    setProductsInsertedInOrderToCalculation([
      ...productsInsertedInOrderToCalculation,
      fkProduto,
    ])
  }

  const updateProductFlags = (quantity: number) => {
    const shouldControlProfitability = quantity > 0 &&
      (jsonOrderForm.operacao.controlaLucratividade || product.produto.controlaLucratividade)

    product.flagAlterado = true
    product.flagPrecoFixo = product.flagPrecoFixo || (shouldControlProfitability && !utilizaTabelaPrecoMobile)
    product.valorUnitario = Number(product.valorUnitario.toFixed(2))
  }

  const ensureProductInCalculationArray = () => {
    const productInArray = productsInsertedInOrderToCalculation?.find(
      (item) => item === product?.fkProduto,
    )

    if (!productInArray) {
      insertProductInArrayToCalculation(product?.fkProduto)
    }
  }

  const paramsFixedValue = paramsLayoutOrderForm?.geral?.find(
    (item) => item.campo === 'fixedValue',
  )

  const paramsButtonAddAndRemove = paramsLayoutOrderForm?.itens?.find(
    (item) => item.campo === 'buttons',
  )

  const visibleStock = paramsLayoutOrderForm?.itens?.find(
    (item) => item.campo === 'availableStock',
  )?.visible ?? false

  const currentQuantityOfProduct = product?.quantidade ?? getProductInOrder()?.quantidade

  const incrementQuantityOfProduct = (quantity?: number) => {
    if (operationType === 'view') return

    ensureProductInCalculationArray()
    setIsChangeProduct(true)
    setUpdatedProductDetails(true)

    const packageQuantity = product?.produto?.qtdeEmbalagem ?? 1
    const newValue = quantity ?? quantityOfProduct + packageQuantity

    product.quantidade = newValue
    updateProductFlags(newValue)
    setQuantityOfProduct(newValue)
  }

  const decrementQuantityOfProduct = (quantity?: number) => {
    if (operationType === 'view') return

    const packageQuantity = product?.produto?.qtdeEmbalagem ?? 1
    const newValue = quantity ?? Math.max(0, quantityOfProduct - packageQuantity)


    if (newValue <= 0) {
      product.flagPrecoFixo = false
      product.quantidade = 0
      product.valorUnitarioBase = utilizaValorSugerido ? product.valorSugerido : product.valorTabela
      product.valorUnitario = utilizaValorSugerido ? product.valorSugerido : (product.valorTabelaDesconto * (100 - product.porcDescontoTabela) / 100);
      product.flagPromocao = false;
      setQuantityOfProduct(0)
      removeProductFromOrder()
      setTimeout(() => {
      }, 1000)
      calculateOrderForm({
        ...jsonOrderForm,
        itemsCalculo: jsonOrderForm.itemsCalculo.filter(
          (item) => item.fkProduto !== product?.fkProduto,
        ),
      }
      )
      return
    }

    ensureProductInCalculationArray()
    setIsChangeProduct(true)
    setUpdatedProductDetails(true)

    product.quantidade = newValue
    updateProductFlags(newValue)
    setQuantityOfProduct(newValue)

  }

  const updateProductInOrder = () => {
    const newJsonOrderForm = {
      ...jsonOrderForm,
      itemsCalculo: jsonOrderForm.itemsCalculo.map((item) =>
        item.fkProduto === product?.fkProduto ? product : item,
      ),
    }

    setJsonOrderForm(newJsonOrderForm)
  }

  const insertProductInOrder = () => {
    const newJsonOrderForm = {
      ...jsonOrderForm,
      itemsCalculo: [...jsonOrderForm.itemsCalculo, product],
    }

    setJsonOrderForm(newJsonOrderForm)
  }

  const removeProductFromOrder = () => {
    const newJsonOrderForm = {
      ...jsonOrderForm,
      itemsCalculo: jsonOrderForm.itemsCalculo.filter(
        (item) => item.produto.idProduto !== product?.produto.idProduto,
      ),
    }
    setJsonOrderForm(newJsonOrderForm)

    const updatedProductsArray = productsInsertedInOrderToCalculation.filter(
      (fkProduto) => fkProduto !== product?.fkProduto,
    )
    setProductsInsertedInOrderToCalculation(updatedProductsArray)

  }

  const changeProductInOrder = () => {
    setIsChangeProduct(false)

    if (!isCalculationOrder) {
      setIsCalculationOrder(true)
    }

    if (getIndexOfCurrentProduct() !== -1) {
      updateProductInOrder()
    } else {
      insertProductInOrder()
    }
  }

  const calculateDateLastBuyProduct = () => {
    if (!product?.ultimoPedido?.data) return 'nunca'

    const lastOrder = new Date(product.ultimoPedido.data)
    const dateNow = new Date()

    const difference = dateNow.getTime() - lastOrder.getTime()
    const days = Math.floor(difference / (1000 * 60 * 60 * 24))

    if (days <= 0) {
      return 'hoje'
    }

    return `há ${days} dias`
  }
  const handleOpenModalDetailsOfProduct = () => {
    setProductIdSelected(product?.fkProduto)
    setIsOpenModalDetailsProduct(true)
  }

  const handleOpenModalProductImage = () => {
    productImageOrderData.action.onSubmitProductImageRequest({
      payload: { productId: product?.fkProduto },
    })
  }

  const handleActivatePromotion = () => {
    if (operationType === 'view') return

    if (product?.quantidade <= 0) {
      toast.info('Insira uma quantidade para aplicar a promoção.')
      return
    }


    product.flagPromocao = true
    product.atualizaValorTabela = true
    product.valorUnitarioBase = product.valorPromocao
    product.valorUnitario = product.valorPromocaoDesconto
    //product.porcDescontoTabela = 0
    product.flagPrecoFixo = false
    changeProductInOrder()
    setIsChangeProduct(true)
    setUpdatedProductDetails(true)
    setQuantityOfProduct(product.quantidade)
    calculateOrderForm({
      ...jsonOrderForm,
      itemsCalculo: jsonOrderForm.itemsCalculo.map((item) =>
        item.fkProduto === product?.fkProduto ? product : item,
      ),
    })
  }

  const handleDisablePromotion = () => {
    if (operationType === 'view') return

    if (product?.quantidade <= 0) {
      removeProductFromOrder()
    }

    //product.porcDescontoTabela = 0

    product.flagPromocao = false
    product.atualizaValorTabela = true
    product.valorUnitarioBase = utilizaValorSugerido ? product.valorSugerido : product.valorTabela;
    product.valorUnitario = product.valorTabelaDesconto
    product.flagPrecoFixo = false
    changeProductInOrder()
    setIsChangeProduct(true)
    setUpdatedProductDetails(true)
    setQuantityOfProduct(product.quantidade)
    calculateOrderForm({
      ...jsonOrderForm,
      itemsCalculo: jsonOrderForm.itemsCalculo.map((item) =>
        item.fkProduto === product?.fkProduto ? product : item,
      ),
    })
  }

  useEffect(() => {
    if (isChangeProduct) {
      // O blur do preço pode substituir o produto antes desta atualização adiada.
      product.quantidade = deferredQuantity
      updateProductFlags(deferredQuantity)
      changeProductInOrder()
    }
  }, [deferredQuantity])

  useEffect(() => {
    setQuantityOfProduct(currentQuantityOfProduct)
  }, [currentQuantityOfProduct])

  return {
    state: {
      product,
      deferredQuantity,
      quantityOfProduct,
      isActiveButton: paramsButtonAddAndRemove?.visible,
      isViewOrder: operationType === 'view',
      isVisibleFixedValue: paramsFixedValue?.visible !== false,
      defaultTabsProductDetails,
    },
    action: {
      onDisablePromotion: handleDisablePromotion,
      onActivatePromotion: handleActivatePromotion,
      onOpenModalProductImage: handleOpenModalProductImage,
      onIncrementQuantityOfProduct: incrementQuantityOfProduct,
      onDecrementQuantityOfProduct: decrementQuantityOfProduct,
      onCalculateDateLastBuyProduct: calculateDateLastBuyProduct,
      onOpenModalDetailsOfProduct: handleOpenModalDetailsOfProduct,
      onChangeDefaultTabsProductDetails: setDefaultTabsProductDetails,
    },
    params: {
      availableStock: visibleStock,
    },
  }
}
